import React, { useEffect, useRef, useState } from 'react'
import * as echarts from 'echarts'
import type { UserService } from '@duck/api-definitions'
import { Card, CardContent, Button } from '@duck/ui'
import { useTar } from '@/modules'
import { KpiStatCard } from './KpiStatCard'
import { CHART_GREEN, CHART_STEEL, CHART_AMBER, CHART_AXIS_TEXT, CHART_GRID_LINE, CHART_AXIS_LINE } from './chartChrome'

type ActiveUsersRange = 'last30' | 'last90' | 'all'

const RANGE_DAYS: Record<ActiveUsersRange, number | undefined> = {
  last30: 30,
  last90: 90,
  all: undefined,
}

interface ActiveUsersSectionProps {
  series: UserService.AdminActiveUsersPoint[]
}

export const ActiveUsersSection: React.FC<ActiveUsersSectionProps> = ({ series }) => {
  const { t } = useTar()
  const [range, setRange] = useState<ActiveUsersRange>('last30')
  const chartRef = useRef<HTMLDivElement | null>(null)

  const latest = series.length > 0 ? series[series.length - 1] : undefined
  const dau = latest?.dau ?? 0
  const wau = latest?.wau ?? 0
  const mau = latest?.mau ?? 0
  const share = (part: number) => (mau > 0 ? part / mau : 0)

  useEffect(() => {
    if (!chartRef.current || series.length === 0) return

    const days = RANGE_DAYS[range]
    const points = days === undefined ? series : series.slice(-days)
    const instance = echarts.init(chartRef.current)

    const makeLine = (name: string, values: number[], color: string): echarts.LineSeriesOption => ({
      name,
      type: 'line',
      data: values,
      showSymbol: false,
      smooth: false,
      lineStyle: { color, width: 2 },
      itemStyle: { color },
    })

    const option: echarts.EChartsOption = {
      tooltip: { trigger: 'axis' },
      legend: { top: 0, textStyle: { color: CHART_AXIS_TEXT } },
      grid: { left: '3%', right: '4%', bottom: '3%', top: 36, containLabel: true },
      xAxis: {
        type: 'category',
        // `date` is a UTC calendar day (YYYY-MM-DD); render it as that same calendar day
        data: points.map(point => new Date(`${point.date}T00:00:00.000Z`).toLocaleDateString(undefined, { timeZone: 'UTC' })),
        axisLabel: { color: CHART_AXIS_TEXT, rotate: 45 },
        axisLine: { lineStyle: { color: CHART_AXIS_LINE } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: { color: CHART_AXIS_TEXT },
        splitLine: { lineStyle: { color: CHART_GRID_LINE } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        makeLine(t('adminKpis.activeUsers.mau'), points.map(point => point.mau), CHART_GREEN),
        makeLine(t('adminKpis.activeUsers.wau'), points.map(point => point.wau), CHART_STEEL),
        makeLine(t('adminKpis.activeUsers.dau'), points.map(point => point.dau), CHART_AMBER),
      ],
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)
    // Same first-paint fix as the other KPI charts: the tab may mount at 0x0
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(instance.getDom())

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      instance.dispose()
    }
  }, [series, range, t])

  const rangeButtons: { value: ActiveUsersRange; label: string }[] = [
    { value: 'last30', label: t('adminKpis.activeUsers.last30Days') },
    { value: 'last90', label: t('adminKpis.activeUsers.last90Days') },
    { value: 'all', label: t('admin.statisticsAllTime') },
  ]

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiStatCard emphasis label={t('adminKpis.activeUsers.mau')} value={mau} tooltip={t('adminKpis.activeUsers.mauTooltip')} fraction={mau > 0 ? 1 : 0} />
        <KpiStatCard label={t('adminKpis.activeUsers.wau')} value={wau} tooltip={t('adminKpis.activeUsers.wauTooltip')} fraction={share(wau)} />
        <KpiStatCard label={t('adminKpis.activeUsers.dau')} value={dau} tooltip={t('adminKpis.activeUsers.dauTooltip')} fraction={share(dau)} />
        <KpiStatCard
          label={t('adminKpis.activeUsers.stickiness')}
          value={`${Math.round(share(wau) * 100)}%`}
          tooltip={t('adminKpis.activeUsers.stickinessTooltip')}
          fraction={share(wau)}
          sublabel={t('adminKpis.activeUsers.stickinessSublabel')}
        />
      </div>

      <Card className="h-full">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-4 gap-2">
            <h3 className="text-lg font-semibold text-foreground">
              {t('adminKpis.activeUsers.chartTitle')}
            </h3>
            <div className="inline-flex gap-1">
              {rangeButtons.map(({ value, label }) => (
                <Button
                  key={value}
                  type="button"
                  variant={range === value ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setRange(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
          {series.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {t('adminKpis.activeUsers.empty')}
            </p>
          ) : (
            <div ref={chartRef} className="w-full h-80" />
          )}
        </CardContent>
      </Card>
    </>
  )
}
